import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  DocumentTextIcon,
  ArrowLeftIcon,
  CloudArrowDownIcon,
  ClipboardDocumentCheckIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { documentsAPI } from '../../services/api'
import useLanguage from '../../hooks/useLanguage'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Badge from '../../components/ui/Badge'

const DocumentDetailPage = () => {
  const { t } = useTranslation('documents')
  const { locale } = useLanguage()
  const { id } = useParams()

  const { data: documentData, isLoading, error } = useQuery(
    ['document', id],
    () => documentsAPI.getDocument(id),
    {
      enabled: !!id,
    }
  )

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'gray',
      processing: 'warning',
      completed: 'success',
      failed: 'error'
    }

    const labels = {
      pending: t('common:status.pending'),
      processing: t('common:status.processing'),
      completed: t('common:status.completed'),
      failed: t('common:status.failed')
    }

    return (
      <Badge variant={variants[status] || 'gray'} size="sm">
        {labels[status] || status}
      </Badge>
    )
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return t('invalidDate')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text={t('loadingDetail')} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-error-600">{t('errorDetail')}</p>
        <Link
          to="/dashboard/documents"
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-table-header)] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1f9f93] mt-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          {t('backToList')}
        </Link>
      </div>
    )
  }

  const document = documentData?.data?.document

  if (!document) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--color-text-muted)]">{t('notFound')}</p>
        <Link
          to="/dashboard/documents"
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-table-header)] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1f9f93] mt-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          {t('backToList')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center space-x-3">
            <Link
              to="/dashboard/documents"
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-table-header)] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1f9f93]"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Link>
            <span className="text-sm font-semibold text-[var(--color-text-muted)] truncate">
              {document.title}
            </span>
          </div>
        </div>
        <div className="mt-4 flex space-x-3 md:ml-4 md:mt-0">
          {document.ocr_status === 'completed' && document.ocrResult && (
            <Button
              as={Link}
              to={`/documents/${document.id}/review`}
              variant={document.receipt?.manually_verified ? 'outline' : 'primary'}
              size="sm"
              leftIcon={
                document.receipt?.manually_verified
                  ? <PencilSquareIcon className="h-4 w-4" />
                  : <ClipboardDocumentCheckIcon className="h-4 w-4" />
              }
            >
              {document.receipt?.manually_verified
                ? t('editReceipt')
                : t('reviewReceipt')}
            </Button>
          )}
          <Button variant="outline" size="sm">
            <CloudArrowDownIcon className="h-4 w-4 mr-2" />
            {t('download')}
          </Button>
        </div>
      </div>

      {/* Document Info */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-medium text-[var(--color-text)]">
            {t('documentInfo')}
          </h3>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('fileName')}</dt>
              <dd className="mt-1 text-sm text-[var(--color-text)]">{document.original_file_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('fileSize')}</dt>
              <dd className="mt-1 text-sm text-[var(--color-text)]">{formatFileSize(document.file_size)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('fileType')}</dt>
              <dd className="mt-1 text-sm text-[var(--color-text)]">{document.mime_type}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('uploadDate')}</dt>
              <dd className="mt-1 text-sm text-[var(--color-text)]">{formatDate(document.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('ocrStatus')}</dt>
              <dd className="mt-1">{getStatusBadge(document.ocr_status)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('category')}</dt>
              <dd className="mt-1 text-sm text-[var(--color-text)]">
                {document.category?.name || t('common:noCategory')}
              </dd>
            </div>
            {document.description && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('description')}</dt>
                <dd className="mt-1 text-sm text-[var(--color-text)]">{document.description}</dd>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* OCR Results */}
      {document.ocrResult && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">
              {t('ocrResults')}
            </h3>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {document.ocrResult.engine && (
                  <div>
                    <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('ocrEngine')}</dt>
                    <dd className="mt-1 text-sm text-[var(--color-text)]">
                      {document.ocrResult.engine === 'easyocr' ? 'EasyOCR' :
                       document.ocrResult.engine === 'paddleocr' ? 'PaddleOCR' :
                       document.ocrResult.engine === 'ai_llm' ? 'AI/LLM' :
                       document.ocrResult.engine}
                    </dd>
                  </div>
                )}
                {document.ocrResult.confidence_score != null && (
                  <div>
                    <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('confidence')}</dt>
                    <dd className="mt-1 text-sm text-[var(--color-text)]">
                      {Math.round(document.ocrResult.confidence_score * 100)}%
                    </dd>
                  </div>
                )}
                {document.ocrResult.processing_time != null && (
                  <div>
                    <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('processingTime')}</dt>
                    <dd className="mt-1 text-sm text-[var(--color-text)]">
                      {(() => {
                        const t = parseFloat(document.ocrResult.processing_time);
                        if (isNaN(t)) return '—';
                        if (t < 60) return `${t.toFixed(1)}s`;
                        return `${Math.floor(t / 60)}m ${Math.round(t % 60)}s`;
                      })()}
                    </dd>
                  </div>
                )}
                {document.ocrResult.language && (
                  <div>
                    <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('language')}</dt>
                    <dd className="mt-1 text-sm text-[var(--color-text)]">
                      {document.ocrResult.language === 'pol' ? t('langPl') :
                       document.ocrResult.language === 'eng' ? t('langEn') :
                       document.ocrResult.language}
                    </dd>
                  </div>
                )}
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--color-text-muted)]">{t('recognizedText')}</dt>
                <dd className="mt-1 text-sm text-[var(--color-text)] whitespace-pre-wrap bg-[var(--color-table-header)] p-3 rounded">
                  {document.ocrResult.raw_text || t('noText')}
                </dd>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default DocumentDetailPage
